import { useInterval } from "@mantine/hooks";
import { useEffect, useState } from "react";

import { MS_IN_SECOND } from "../constants";
import { formatTime } from "../formatters";

// shared ticker state for all useStopwatch instances
type TickListener = () => void;
const listeners = new Set<TickListener>();

// Get current time floored to the second for consistent timing
function getCurrentTime(): number {
    return Math.floor(Date.now() / 1000) * 1000;
}

// Calculate seconds elapsed since startTime, with safety bounds
function getElapsedTime(startTime?: number | null): number {
    if (!startTime) return 0;

    const now = getCurrentTime();
    const elapsedTime = Math.floor((now - startTime) / 1000);
    return Math.max(elapsedTime, 0); // Ensure we never show negative time
}

/**
 * Internal hook to manage the shared ticker using Mantine's useInterval
 * Creates a central timer that all useStopwatch instances subscribe to
 */
function useSharedTicker() {
    // Use interval with autoInvoke if we already have listeners
    const interval = useInterval(
        // Inline notification function responsible for notifying all listeners
        () =>
            listeners.forEach((listener) => {
                listener();
            }),
        MS_IN_SECOND,
        { autoInvoke: listeners.size > 0 },
    );

    return {
        subscribe: (listener: TickListener) => {
            // Start interval if this was the first listener added
            listeners.add(listener);
            if (!interval.active) {
                interval.start();
            }
            // Return unsubscribe function
            return () => {
                listeners.delete(listener);

                // Stop interval when no listeners remain, check if interval is active to avoid edge cases
                if (interval.active && listeners.size === 0) {
                    interval.stop();
                }
            };
        },
    };
}

/**
 * A hook that returns the time elapsed since the startTime. If no start time is provided the timer will start from 0.
 * Hiding a stopwatch with no start time should be intentionally handled by the consuming component.
 * Multiple instances use a shared ticker to ensure synchronized updates.
 * @param startTime epoch time in ms that the timer should start from
 * @param appendUnit Whether to append the unit to the formatted string (ie. sec or min)
 * @param format The format to use. see: https://day.js.org/docs/en/display/format
 * @returns A formatted string representing the elapsed time since startTime
 */
export function useStopwatch(startTime?: number | null, appendUnit = false, format?: string): string {
    const [secondsElapsed, setSecondsElapsed] = useState(() => getElapsedTime(startTime));
    const ticker = useSharedTicker();

    useEffect(() => {
        // Update immediately when startTime changes
        setSecondsElapsed(getElapsedTime(startTime));

        // Create listener that updates the elapsed time on each tick
        const listener = () => setSecondsElapsed(getElapsedTime(startTime));

        // Subscribe to shared ticker and get unsubscribe function
        const unsubscribe = ticker.subscribe(listener);

        // Clean up subscription when component unmounts or startTime changes
        return unsubscribe;
    }, [startTime, ticker]);

    return formatTime(secondsElapsed, appendUnit, format);
}
